import { groupBy } from "lodash-es";
import { useHistory, useLocation } from "react-router-dom";
import { BulkClassQuery } from "src/api/Class";
import { StudentInfoRequestsQuery } from "src/api/StudentInfoRequest";
import BackButton from "src/components/buttons/BackButton";
import { BorderButton } from "src/components/buttons/BorderButton";
import { AddItemHeader } from "src/components/forms/AddItemHeader";
import Container from "src/components/layout/Container";
import { BulkEditCheckoutModal } from "src/components/modals/BulkEditCheckoutModal";
import { useApi } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import { modals } from "src/utils/modals";
import { useSearchParams } from "src/utils/useSearchParams";
import {
  InfoRequestType,
  bulkClassVariables,
  studentInfoRequests,
  studentInfoRequestsVariables,
} from "types/code-generator";

interface Props {}

export default function BulkEditInfoRequests(props: Props) {
  const history = useHistory();
  const location = useLocation();
  const store = useStore();
  const params = useSearchParams();

  const ids = params.getAll("id");
  const excluded = params.has("exclude");

  const infoRequestsVariables: studentInfoRequestsVariables = {
    where: {
      organizationId: store.organization.id,
      departmentId: undefined,
    },
  };

  const queryInfoRequests = useApi(StudentInfoRequestsQuery, {
    variables: infoRequestsVariables,
  });

  const variables: bulkClassVariables = {
    filter: location.search,
    where: {
      organizationId: store.organization.id,
      id:
        ids.length > 0
          ? excluded
            ? { notIn: ids }
            : {
                in: ids,
              }
          : undefined,
    },
  };

  const bulkEditQuery = useApi(BulkClassQuery, {
    variables,
  });

  const counts = bulkEditQuery.bulkClass;
  const infoRequests = queryInfoRequests.studentInfoRequests || [];

  function onCancel() {
    history.goBack();
  }

  function onNetworkAction() {
    bulkEditQuery.refetch();
  }

  function bulkEditAgreement(
    infoRequest: studentInfoRequests["studentInfoRequests"][0]
  ) {
    modals.show(BulkEditCheckoutModal, {
      field: "infoRequests",
      counts,
      itemId: infoRequest.id,
      itemTitle: infoRequest.description,
      onNetworkAction,
    });
  }

  const groupedInfoRequests = groupBy(infoRequests, "department.id");

  // console.log("offering", offering);
  return (
    <>
      <AddItemHeader>
        <Container large>
          <BackButton backText="Return to Bulk Edit" />
          <h2 className="mb-0 flex-1">Info Requests</h2>
        </Container>
      </AddItemHeader>

      <Container large className="bg-white pb-24 pt-24">
        <Container fullSmall>
          <p className="pt-4">
            Select an info request to enable or disable for all selected
            offerings.
          </p>

          <div>
            {Object.keys(groupedInfoRequests).map((departmentId) => {
              let total = counts?.total || 0;

              if (store.organization.hasDepartments) {
                total = counts?.totalPerDepartment[departmentId] || 0;
              }

              let header = (
                <>
                  <hr className="my-0" />
                </>
              );
              const infoRequests = groupedInfoRequests[departmentId].filter(
                (a) => !a.availableForAllOfferings
              );

              if (store.organization.hasDepartments) {
                header = (
                  <div>
                    <div className="step-subhead">
                      {groupedInfoRequests[departmentId][0].department.title}
                    </div>
                    <hr className="mt-0 border-darkGray" />
                    <p className="px-4">
                      {total.toLocaleString()} from your selected list may be
                      edited to enable/disable these settings.
                    </p>
                    <hr className="my-0" />
                  </div>
                );
              }

              return (
                <div>
                  {header}
                  <div className="divide-y divide-borderGray">
                    {infoRequests.map((a) => {
                      let count =
                        counts?.infoRequests[departmentId]?.[a.id]?.length || 0;
                      let total = counts?.totalPerDepartment[departmentId] || 0;

                      let type = a.type ? a.type.toString() : "Free Text Field";
                      let description = [];

                      if (type === InfoRequestType.Predefined) {
                        type = "Predefined Answers";

                        description = [...a.answers];
                      } else if (type === InfoRequestType.YesNo) {
                        type = "Yes/No";

                        if (a.yesQuestion) {
                          description.push(`If yes, ”${a.yesQuestion}”`);
                        }

                        if (a.noQuestion) {
                          description.push(`If no, ”${a.noQuestion}”`);
                        }
                      } else if (type === InfoRequestType.Text) {
                        type = "Free Text Field";
                      }

                      let enabled = `Enabled on ${count.toLocaleString()} of ${total.toLocaleString()} selected offering${
                        count !== 1 ? "s" : ""
                      }.`;

                      if (count === total) {
                        enabled = `Enabled on all ${total.toLocaleString()} selected offering${
                          total !== 1 && "s"
                        }.`;
                      }

                      return (
                        <button
                          key={a.id}
                          className="flex w-full items-center px-4 py-4 text-left"
                          type="button"
                          onClick={() => bulkEditAgreement(a)}
                        >
                          <div className="flex-1 pr-8">
                            <strong className="text-dark">
                              {a.description}
                            </strong>
                            <div className="text-sm">
                              <strong>{type}</strong>{" "}
                              {description.length > 0 &&
                                ` • ${description.join(" • ")}`}
                            </div>

                            <div className="mb-2 text-sm text-empty">
                              {enabled}
                            </div>
                          </div>
                          <i className="far fa-chevron-right text-organization" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <hr className="my-0" />

          <div className="pb-12 pt-8">
            <BorderButton onClick={onCancel}>Return to Bulk Edit</BorderButton>
          </div>
        </Container>
      </Container>
    </>
  );
}
